import {RTC_PROTOCOL_V1, type CallIntent, type GroupCallState, type RoomCallSummary} from '@phonehq/rtc-core';
import type {RtcFeatureFlags} from './rtcFeatureFlags';

export type RoomCallEntry =
    | {readonly kind: 'hidden'}
    | {readonly kind: 'one_to_one'; readonly video: boolean}
    | {readonly kind: 'group_start'; readonly video: boolean; readonly busy: boolean}
    | {readonly kind: 'group_join'; readonly intent: CallIntent; readonly peopleCount: number; readonly busy: boolean}
    | {readonly kind: 'group_full'; readonly maxParticipants: number}
    | {readonly kind: 'group_in_call'}
    | {readonly kind: 'group_unavailable'; readonly reason: 'not_owner' | 'no_session'};

export interface RoomCallEntryInput {
    readonly hasRoom: boolean;
    readonly isOneToOne: boolean;
    readonly legacyDirectAvailable: boolean;
    readonly features: RtcFeatureFlags;
    readonly summary?: RoomCallSummary;
    readonly peopleInCall: number;
    readonly inThisCall: boolean;
    readonly tabOwner: boolean;
    readonly sessionReady: boolean;
    readonly busy?: boolean;
    readonly maxParticipants?: number;
}

export const selectRoomCallEntry = (input: RoomCallEntryInput): RoomCallEntry => {
    const legacy: RoomCallEntry = input.legacyDirectAvailable
        ? {kind: 'one_to_one', video: input.features.videoCalls}
        : {kind: 'hidden'};
    if (!input.features.groupCalls || !input.hasRoom || input.isOneToOne) {
        return legacy;
    }
    if (input.inThisCall) {
        return {kind: 'group_in_call'};
    }
    if (!input.sessionReady) {
        return {kind: 'group_unavailable', reason: 'no_session'};
    }
    if (!input.tabOwner) {
        return {kind: 'group_unavailable', reason: 'not_owner'};
    }
    const summary = input.summary;
    if (!summary || summary.participantCount === 0) {
        return {kind: 'group_start', video: input.features.videoCalls, busy: input.busy ?? false};
    }
    const maxParticipants = input.maxParticipants ?? RTC_PROTOCOL_V1.maxParticipants;
    if (summary.userIds.length >= maxParticipants && !summary.includesMe) {
        return {kind: 'group_full', maxParticipants};
    }
    const intent: CallIntent = input.features.videoCalls ? (summary.intent ?? 'audio') : 'audio';
    return {kind: 'group_join', intent, peopleCount: input.peopleInCall, busy: input.busy ?? false};
};

export interface DirectRoomShape {
    readonly isDirect: boolean;
    readonly joinedHumanMembers: number;
}

export const isOneToOneRoom = ({isDirect, joinedHumanMembers}: DirectRoomShape): boolean =>
    isDirect && joinedHumanMembers <= 2;

export const isInCallPhase = (phase: GroupCallState['phase']): boolean => phase === 'joined' || phase === 'reconnecting';

export const isInThisCall = (call: Pick<GroupCallState, 'roomId' | 'phase'>, roomId: string | null | undefined): boolean =>
    !!roomId && call.roomId === roomId && isInCallPhase(call.phase);

export const showsCallBadge = (entry: RoomCallEntry): boolean =>
    entry.kind !== 'group_join' && entry.kind !== 'group_in_call' && entry.kind !== 'group_full';
